// /social-leads — everyone who wrote to our messenger numbers (Wepster):
// list · conversation · link to a portal client. Answers go out of the same
// messenger account the person wrote to.
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { ExternalLink, Link2, Loader2, MessagesSquare, RefreshCw, Search, Unlink } from "lucide-react";
import { Composer, MessageList, MessengerBadge, MESSENGER_LABEL, openInWepster } from "../components/WepsterChat";

const errText = (e) => e?.response?.data?.detail || e?.message || "Request failed";
const fmtDay = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

function ClientPicker({ onPick }) {
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);
  useEffect(() => {
    api.get("/sales-clients", { params: { client_type: "any" } })
      .then((r) => setClients(Array.isArray(r.data) ? r.data : (r.data?.clients || [])))
      .catch(() => setClients([]));
  }, []);
  const hits = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return [];
    return clients.filter((c) => (c.name || "").toLowerCase().includes(n)
      || (c.phone || "").replace(/\D/g, "").includes(n.replace(/\D/g, "") || "~")).slice(0, 8);
  }, [q, clients]);
  return (
    <div className="relative">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Link to client…"
             className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm w-56" />
      {hits.length > 0 && (
        <div className="absolute z-20 mt-1 w-72 rounded-md border border-zinc-700 bg-zinc-900 shadow-lg">
          {hits.map((c) => (
            <button key={c.id} onClick={() => { onPick(c); setQ(""); }}
                    className="block w-full text-left px-3 py-1.5 text-sm text-zinc-200 hover:bg-zinc-800">
              {c.name}{c.phone ? <span className="text-zinc-500"> · {c.phone}</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SocialLeadsPage() {
  const [params, setParams] = useSearchParams();
  const selected = params.get("lead");
  const [status, setStatus] = useState(null);
  const [list, setList] = useState({ leads: [], total: 0, messengers: {} });
  const [q, setQ] = useState("");
  const [linked, setLinked] = useState("");
  const [messenger, setMessenger] = useState("");
  const [allContacts, setAllContacts] = useState(false); // include address-book-only contacts
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadList = async () => {
    try {
      const r = await api.get("/wepster/leads", {
        params: { q: q || undefined, linked: linked || undefined, messenger: messenger || undefined,
                  contacts: allContacts ? "all" : undefined, limit: 100 },
      });
      setList(r.data);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (wid) => {
    if (!wid) { setDetail(null); return; }
    setDetailLoading(true);
    try {
      const r = await api.get(`/wepster/leads/${wid}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(errText(e));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    api.get("/wepster/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  }, []);
  useEffect(() => {
    const t = setTimeout(loadList, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, linked, messenger, allContacts]);
  useEffect(() => { loadDetail(selected); }, [selected]);

  const select = (wid) => {
    const next = new URLSearchParams(params);
    if (wid) next.set("lead", wid); else next.delete("lead");
    setParams(next, { replace: true });
  };

  const link = async (clientId) => {
    try {
      const r = await api.post(`/wepster/leads/${selected}/link`, { client_id: clientId });
      if (r.data?.warning) toast.warning(r.data.warning); else toast.success(clientId ? "Linked to client" : "Unlinked");
      loadDetail(selected);
      loadList();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const refresh = async () => {
    try {
      await api.post(`/wepster/leads/${selected}/refresh`);
      loadDetail(selected);
      loadList();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const send = async (text) => {
    await api.post(`/wepster/leads/${selected}/messages`, { text });
    loadDetail(selected);
    loadList();
  };

  const lead = detail?.lead;

  return (
    <div data-testid="social-leads-page" className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope'] flex items-center gap-3">
            <MessagesSquare className="h-6 w-6 text-sky-400" />Messenger leads
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Everyone who wrote to our Telegram / Viber / WhatsApp numbers in Wepster.
            {status?.counts ? ` ${status.counts.leads} chats · ${status.counts.linked} linked to clients.` : ""}
          </p>
        </div>
      </div>

      {status && !status.configured && (
        <Card className="bg-zinc-900 border-amber-500/30">
          <CardContent className="py-3 text-sm text-amber-300">
            Wepster is not connected yet — an admin adds the API token on Settings → Wepster.
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="h-4 w-4 text-zinc-500 absolute left-2 top-2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, phone, email"
                 className="bg-zinc-900 border-zinc-700 text-zinc-50 h-8 text-sm pl-8 w-60" />
        </div>
        {[["", "All"], ["no", "Not linked"], ["yes", "Linked"]].map(([v, l]) => (
          <button key={v} onClick={() => setLinked(v)}
                  className={cn("text-xs px-2.5 py-1 rounded-md border",
                    linked === v ? "border-sky-500/50 text-sky-300 bg-sky-500/10" : "border-zinc-700 text-zinc-400")}>
            {l}
          </button>
        ))}
        <span className="w-px h-5 bg-zinc-800 mx-1" />
        {[["", "Any channel"], ...Object.keys(list.messengers || {}).filter((m) => m && m !== "unknown").map((m) => [m, `${MESSENGER_LABEL[m] || m} · ${list.messengers[m]}`])].map(([v, l]) => (
          <button key={v || "any"} onClick={() => setMessenger(v)}
                  className={cn("text-xs px-2.5 py-1 rounded-md border",
                    messenger === v ? "border-sky-500/50 text-sky-300 bg-sky-500/10" : "border-zinc-700 text-zinc-400")}>
            {l}
          </button>
        ))}
      </div>

      {(list.address_book_hidden > 0 || allContacts) && (
        <button onClick={() => setAllContacts((v) => !v)} className="text-xs text-zinc-500 hover:text-zinc-300" data-testid="toggle-address-book">
          {allContacts
            ? "Hide address-book contacts (never wrote)"
            : `+ ${list.address_book_hidden} address-book contacts from personal accounts who never wrote — show`}
        </button>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
        <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-zinc-800">
            {loading ? (
              <p className="text-zinc-500 py-8 text-center text-sm">Loading...</p>
            ) : list.leads.length === 0 ? (
              <p className="text-zinc-500 py-8 text-center text-sm">No chats</p>
            ) : list.leads.map((l) => (
              <button key={l.wepster_id} onClick={() => select(l.wepster_id)}
                      className={cn("w-full text-left px-3 py-2 hover:bg-zinc-800/60",
                        selected === l.wepster_id && "bg-sky-500/10")}>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-zinc-100 truncate">{l.name || l.phone || "—"}</span>
                  <MessengerBadge type={l.messenger_type} />
                  <span className="ml-auto text-[10px] text-zinc-500 shrink-0">{fmtDay(l.last_message_at || l.first_seen_at)}</span>
                </div>
                <div className="text-xs text-zinc-500 truncate mt-0.5">
                  {l.last_message_direction === "out" ? "You: " : ""}{l.last_message_text || "—"}
                </div>
                {l.client_name && <div className="text-[11px] text-emerald-400 mt-0.5 truncate">↳ {l.client_name}</div>}
              </button>
            ))}
          </div>
          {list.total > list.leads.length && (
            <p className="text-[11px] text-zinc-500 px-3 py-1.5 border-t border-zinc-800">
              Showing {list.leads.length} of {list.total} — narrow with search.
            </p>
          )}
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            {!selected ? (
              <p className="text-zinc-500 py-16 text-center text-sm">Pick a chat on the left</p>
            ) : detailLoading && !detail ? (
              <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>
            ) : !lead ? null : (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-zinc-50 font-medium">{lead.name || "—"}</span>
                  <MessengerBadge type={lead.messenger_type} />
                  {lead.phone && <span className="text-xs text-zinc-400">+{lead.phone}</span>}
                  {lead.email && <span className="text-xs text-zinc-400">{lead.email}</span>}
                  <div className="ml-auto flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-7 text-zinc-400" onClick={refresh}>
                      <RefreshCw className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-zinc-400" onClick={() => openInWepster(lead.wepster_id)}>
                      <ExternalLink className="h-3.5 w-3.5 mr-1" />Wepster
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  {lead.client_id ? (
                    <>
                      <a href={`/clients?client=${lead.client_id}`} className="text-emerald-400 hover:underline flex items-center gap-1">
                        <Link2 className="h-3 w-3" />{lead.client_name || "Client"}
                      </a>
                      <span className="text-zinc-600">({lead.link_source === "phone" ? "matched by phone" : lead.link_source || "linked"})</span>
                      <Button size="sm" variant="ghost" className="h-6 text-zinc-500" onClick={() => link(null)}>
                        <Unlink className="h-3 w-3 mr-1" />Unlink
                      </Button>
                    </>
                  ) : (
                    <ClientPicker onPick={(c) => link(c.id)} />
                  )}
                  {(lead.tags || []).map((t) => (
                    <span key={t} className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400">{t}</span>
                  ))}
                </div>
                <div className="max-h-[55vh] overflow-y-auto pr-1 border-t border-zinc-800 pt-3">
                  <MessageList messages={detail.messages} />
                </div>
                <Composer onSend={send} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
